import React, { useEffect, useState } from 'react';

import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import {
  fetchCaseSmileFrontPhotoUrl,
  patientInitialsFromName,
} from '../../../lib/patientProfilePhoto';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import PrescriptionForm from './PrescriptionForm';
import {
  buildTreatmentSpecsPayloadFromCase,
  formSnapshotFromCase,
} from '../../../lib/casePrescriptionPayload';
import { canEditPrescriptionDuringSubmission } from '../../../lib/casePrescriptionEditPolicy';
import { getCasePrioritySelectOptions } from '../../../lib/casePriority';

const DetailsTab = ({ caseData, userRole, onSave, timelinePhaseItems = [] }) => {
 const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    caseType: caseData?.caseType,
    treatmentType: caseData?.treatmentType,
    priority: caseData?.priority,
    notes: caseData?.notes,
    archToTreat: caseData?.archToTreat,
    patientName: caseData?.patientName,
    patientDOB: caseData?.patientDOB,
    patientId: caseData?.patientId,
    patientContact: caseData?.patientContact
  });
  const [prescriptionData, setPrescriptionData] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);

  const patientInitials = patientInitialsFromName(caseData?.patientName);

  useEffect(() => {
    const caseId = caseData?.id;
    if (!caseId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const url = await fetchCaseSmileFrontPhotoUrl(caseId);
        if (cancelled) {
          if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
          return;
        }
        setAvatarUrl((prev) => {
          if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (err) {
        console.error('Error loading case smile photo:', err);
        if (!cancelled) setAvatarUrl(null);
      }
    })();

    return () => {
      cancelled = true;
      setAvatarUrl((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [caseData?.id]);

  const canEdit = userRole === 'dentist_client' || userRole === 'administrator';

  const submissionEditable = canEditPrescriptionDuringSubmission({
    userRole,
    caseData,
    timelinePhaseItems,
  });

  const allowPrescriptionEdit = isEditing || submissionEditable;

  const handleSave = ({ prescriptionOnly = false } = {}) => {
    const base =
      prescriptionOnly && caseData ? formSnapshotFromCase(caseData) : formData;
    const specs =
      prescriptionData ?? buildTreatmentSpecsPayloadFromCase(caseData);
    onSave({
      ...base,
      ...(prescriptionOnly && prescriptionData
        ? {
            archToTreat: prescriptionData.archToTreat ?? base.archToTreat,
            treatmentType: prescriptionData.treatmentType ?? base.treatmentType,
          }
        : {}),
      treatmentSpecs: specs,
    });
    if (!prescriptionOnly) setIsEditing(false);
  };

  const caseTypeOptions = [
    { value: 'aligners', label: 'Aligneurs Transparents' },
    { value: 'braces', label: 'Brackets' },
    { value: 'retainer', label: 'Appareil de Contention' },
    { value: 'other', label: 'Autre' }
  ];

  const treatmentTypeOptions = [
    { value: 'Full Arch', label: 'Arcade Complète' },
    { value: 'Partial', label: 'Traitement Partiel' },
    { value: 'Comprehensive', label: 'Traitement Complet' },
    { value: 'Retention', label: 'Contention' }
  ];

  const archOptions = [
     { value: 'upper', label: 'Arcade Supérieure' },
  { value: 'lower', label: 'Arcade Inférieure' },
  { value: 'both', label: 'Les Deux Arcades' }
  ];
  

  const priorityOptions = getCasePrioritySelectOptions(formData?.priority);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Case Details</h2>
        {canEdit && !isEditing && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
           Modifier les détails
          </Button>
        )}
        {submissionEditable && !isEditing && (
          <p className="text-xs text-muted-foreground max-w-md text-right hidden sm:block">
            Prescription modifiable tant que la phase de soumission n’est pas finalisée.
          </p>
        )}
        {isEditing && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(false)}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Save"
              iconPosition="left"
              onClick={() => handleSave({ prescriptionOnly: false })}
            >
              Enregistrer
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="p-4 rounded-lg case-detail-section-surface">
            <h3 className="text-sm font-medium text-foreground mb-3">Patient Information</h3>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : patientInitials ? (
                    <span className="text-2xl font-semibold text-muted-foreground">
                      {patientInitials}
                    </span>
                  ) : (
                    <Icon name="User" size={32} className="text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <div className="flex justify-between gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">Nom complet:</span>
                  <span className="text-sm font-medium text-foreground text-right">
                    {caseData?.patientName}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">Date de naissance:</span>
                  <span className="text-sm font-medium text-foreground text-right">
                    {caseData?.patientDOB}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">Patient ID:</span>
                  <span className="text-sm font-medium text-foreground text-right">
                    {caseData?.patientId}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">Contact:</span>
                  <span className="text-sm font-medium text-foreground text-right">
                    {caseData?.patientContact}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {isEditing ? (
            <>
              <Select
                label="Type de Conception"
                options={caseTypeOptions}
                value={formData?.caseType}
                onChange={(value) => setFormData({ ...formData, caseType: value })}
              />
              <Select
                label="Type de Traitement"
                options={treatmentTypeOptions}
                value={formData.treatmentType}
                onChange={(value) => setFormData({ ...formData, treatmentType: value })}
              />
               <Select
                label="Arcade à Traiter"
                options={archOptions}
                value={formData?.archToTreat}
                onChange={(value) => setFormData({ ...formData, archToTreat: value })}
              />
              <Select
                label="Niveau de Priorité"
                options={priorityOptions}
                value={formData?.priority}
                onChange
                ={(value) => setFormData({ ...formData, priority: value })}
              />
              
             
            </>
          ) : (
            <div className="p-4 rounded-lg case-detail-section-surface">
              <h3 className="text-sm font-medium text-foreground mb-3">Spécifications du conception</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Type de Conception:</span>
                  <span className="text-sm font-medium text-foreground">{caseData?.caseTypeLabel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Type de Traitement:</span>
                  <span className="text-sm font-medium text-foreground">{caseData?.treatmentTypeLabel}</span>
                  </div>

                     <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Arcade à Traiter:</span>
                    <span className="text-sm font-medium text-foreground"> 
                     {caseData?.archToTreatLabel || 'Non défini'}

                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Niveau de Priorité:</span>
                    <span className="text-sm font-medium text-foreground">{caseData?.priorityLabel}</span>
                  </div>
                  
               


              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="p-4 rounded-lg case-detail-section-surface">
            <h3 className="text-sm font-medium text-foreground mb-3">Détails de l'assignation</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Dentiste:</span>
                <span className="text-sm font-medium text-foreground">{caseData?.dentistName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Personnel assigné:</span>
                <span className="text-sm font-medium text-foreground">{caseData?.assignedStaff}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Prosthetist:</span>
                <span className="text-sm font-medium text-foreground">{caseData?.prosthetist}</span>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg case-detail-section-surface">
            <h3 className="text-sm font-medium text-foreground mb-3">Chronologie</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Date de création :</span>
                <span className="text-sm font-medium text-foreground">{caseData?.createdDate}</span>
              </div>
          
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Dernière mise à jour:</span>
                <span className="text-sm font-medium text-foreground">{caseData?.lastUpdated}</span>
              </div>
            </div>
          </div>

          {isEditing ? (
            <Input
              label="Notes Cliniques"
              type="text"
              value={formData?.notes}
              onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
             
            />
          ) : (
            <div className="p-4 rounded-lg case-detail-section-surface">
              <h3 className="text-sm font-medium text-foreground mb-2">Notes Cliniques</h3>
              <p className="text-sm text-muted-foreground">{caseData?.notes}</p>
            </div>
          )}
        </div>
      </div>
       <PrescriptionForm
        caseData={caseData}
        onDataChange={setPrescriptionData}
        allowPrescriptionEdit={allowPrescriptionEdit}
      />

      {submissionEditable && !isEditing && (
        <div className="flex justify-end mt-4">
          <Button
            variant="default"
            size="sm"
            iconName="Save"
            iconPosition="left"
            onClick={() => handleSave({ prescriptionOnly: true })}
          >
            Enregistrer la prescription
          </Button>
        </div>
      )}
    </div>
  );
};

export default DetailsTab;